import React from 'react';

/**
 * Outreach images block — rendered between the title/type-label block and the
 * description on the public outreach page, the wizard live preview (step 5),
 * and the preview modal (one component so the three header layouts never
 * drift; PRD OUTREACH_UPDATES_2026-09 §B.4.5).
 *
 * Org-branded public surface: plain elements, deliberately outside the app
 * design system (same rationale as the rest of the public outreach page).
 * Stacked full-width in builder order, no captions (alt text only).
 */

export interface OutreachDisplayImage {
  url: string;
  alt?: string;
}

interface Props {
  images?: OutreachDisplayImage[] | null;
}

/** Hides itself on load error so a deleted-from-storage key degrades to today's layout. */
const OutreachImage: React.FC<OutreachDisplayImage> = ({ url, alt }) => {
  const [failed, setFailed] = React.useState(false);
  if (failed) return null;
  return (
    <img
      src={url}
      alt={alt || ''}
      loading="lazy"
      className="w-full max-h-96 object-contain rounded-lg"
      onError={() => setFailed(true)}
    />
  );
};

const OutreachImages: React.FC<Props> = ({ images }) => {
  // No images → render nothing at all (zero layout shift, no empty container)
  if (!images || images.length === 0) return null;

  return (
    <div className="mt-4 space-y-4">
      {images.map((img, i) => (
        <OutreachImage key={`${img.url}-${i}`} url={img.url} alt={img.alt} />
      ))}
    </div>
  );
};

export default OutreachImages;
